import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell } from 'lucide-react';
import { ownerApi } from '@/api/ownerApi';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import type { OwnerNotification } from '@/types/owner';
import { queryKeys } from '@/utils/queryKeys';

const RECENT_NOTIFICATION_LIMIT = 10;

/**
 * Reads the sole human-readable payload field the notification UI permits.
 * Contact-shaped keys (name/email/phone/LinkedIn) and arbitrary server data are
 * never interpolated into the DOM, even if a malformed payload contains them.
 */
function notificationProjectName(data: OwnerNotification['data']): string {
  const projectName = data?.projectName;
  if (typeof projectName !== 'string') return 'your project';
  const trimmed = projectName.trim();
  return trimmed ? trimmed.slice(0, 120) : 'your project';
}

/** Fixed event vocabulary: never trust/render an arbitrary message from `data`. */
function notificationCopy(notification: OwnerNotification): string {
  const projectName = notificationProjectName(notification.data);
  switch (notification.type) {
    case 'connect_request_new':
      return `New connect request on ${projectName}`;
    case 'connect_request_accepted':
      return `Connect request accepted on ${projectName}`;
    case 'claim_created':
      return `New claim created for ${projectName}`;
    case 'claim_approved':
      return `Claim approved for ${projectName}`;
  }
}

function notificationDate(createdAt: string): string | null {
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() === new Date().getFullYear() ? undefined : 'numeric',
  }).format(date);
}

/**
 * Owner-console notification center. One owner-scoped GET supplies both the list
 * and unread badge so they are a consistent snapshot; marking an item invalidates
 * the broad owner namespace to keep both list/unread projections coherent.
 */
export function NotificationBell() {
  const queryClient = useQueryClient();
  const notificationsQuery = useQuery({
    queryKey: queryKeys.notifications.owner.list(RECENT_NOTIFICATION_LIMIT, 0),
    queryFn: () => ownerApi.getOwnerNotifications(RECENT_NOTIFICATION_LIMIT, 0),
    staleTime: 15_000,
    refetchInterval: 60_000,
  });

  const markRead = useMutation({
    mutationFn: (notificationId: number) => ownerApi.markNotificationRead(notificationId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.owner.all }),
  });

  const notifications = notificationsQuery.data?.notifications ?? [];
  const unreadCount = notificationsQuery.data?.unreadCount ?? 0;
  const triggerLabel =
    unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={triggerLabel}
          className="relative flex h-9 w-9 items-center justify-center rounded-[2px] text-[color:rgba(247,245,240,0.72)] transition-colors duration-[var(--l-dur-fast)] hover:bg-white/5 hover:text-[color:var(--l-paper)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--l-gold)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--l-ink)]"
        >
          <Bell className="h-[18px] w-[18px]" aria-hidden="true" />
          {unreadCount > 0 ? (
            <span
              aria-hidden="true"
              className="ledger-figure absolute -right-1 -top-1 flex min-h-4 min-w-4 items-center justify-center rounded-full bg-[color:var(--l-gold)] px-1 text-[9px] font-bold leading-4 text-[#1a1a2e]"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          ) : null}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        sideOffset={8}
        aria-label="Owner notifications"
        className="w-[min(22rem,calc(100vw-1rem))] rounded-[3px] border-[#ded9ce] bg-[#fbfaf6] p-0 text-[#1a1a2e] shadow-[0_12px_28px_-12px_rgba(26,26,46,0.28),0_2px_6px_rgba(26,26,46,0.08)] dark:border-[#33333f] dark:bg-[#1c1c29] dark:text-[#f4f1ea]"
      >
        <DropdownMenuLabel className="flex items-baseline justify-between px-4 py-3">
          <span className="ledger-serif text-base font-semibold">Notifications</span>
          <span className="ledger-sc text-[10px] font-semibold tracking-[0.12em] text-[#6b7280] dark:text-[#a6a29a]">
            Recent
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="m-0 bg-[#ded9ce] dark:bg-[#33333f]" />

        {notificationsQuery.isPending ? (
          <div
            role="status"
            className="px-4 py-8 text-center text-sm text-[#6b7280] dark:text-[#a6a29a]"
          >
            Loading notifications…
          </div>
        ) : notificationsQuery.isError ? (
          <div
            role="alert"
            className="px-4 py-8 text-center text-sm text-[#6b7280] dark:text-[#a6a29a]"
          >
            Notifications are unavailable.
          </div>
        ) : notifications.length === 0 ? (
          <div
            role="status"
            className="px-4 py-8 text-center text-sm text-[#6b7280] dark:text-[#a6a29a]"
          >
            You're all caught up.
          </div>
        ) : (
          <div className="max-h-[22rem] overflow-y-auto">
            {notifications.map((notification) => {
              const copy = notificationCopy(notification);
              const isUnread = notification.status === 'unread';
              const isMarking = markRead.isPending && markRead.variables === notification.id;
              const date = notificationDate(notification.createdAt);

              return (
                <DropdownMenuItem
                  key={notification.id}
                  disabled={isMarking}
                  aria-label={isUnread ? `${copy}, mark as read` : `${copy}, read`}
                  onSelect={(event) => {
                    event.preventDefault();
                    if (isUnread && !isMarking) markRead.mutate(notification.id);
                  }}
                  className={cn(
                    'relative items-start gap-3 rounded-none border-b border-[#e7e3d9] px-4 py-3 last:border-b-0 focus:bg-[#f6ecd6] focus:text-[#1a1a2e] dark:border-[#2a2a35] dark:focus:bg-[#2a2413] dark:focus:text-[#f4f1ea]',
                    isUnread ? 'cursor-pointer' : 'cursor-default opacity-70',
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      'mt-1.5 h-2 w-2 shrink-0 rounded-full border',
                      isUnread
                        ? 'border-[#c6851c] bg-[#e9a634] dark:border-[#d69a2c] dark:bg-[#f0b84e]'
                        : 'border-[#9ca3af] bg-transparent dark:border-[#7f7c74]',
                    )}
                  />
                  <span className="min-w-0 flex-1">
                    <span className={cn('block text-sm leading-5', isUnread && 'font-semibold')}>
                      {copy}
                    </span>
                    {date ? (
                      <time
                        dateTime={notification.createdAt}
                        className="ledger-figure mt-1 block text-[11px] text-[#6b7280] dark:text-[#a6a29a]"
                      >
                        {date}
                      </time>
                    ) : null}
                  </span>
                </DropdownMenuItem>
              );
            })}
          </div>
        )}

        {markRead.isError ? (
          <div
            role="alert"
            className="border-t border-[#ded9ce] px-4 py-2 text-xs text-[#a2513f] dark:border-[#33333f] dark:text-[#c56a54]"
          >
            Could not mark notification as read.
          </div>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
